/**
 * Los permisos que la app necesita en este aparato, juntos: avisos y camara.
 *
 * Lo que el navegador permite: pedirlos SOLO tras un toque de la persona, y
 * nunca volver a preguntar lo que ya se nego —eso se cambia en el sistema—. Por
 * eso Configuracion tiene un boton para lo que falta y, para lo bloqueado, la
 * guia de `unblock-steps.ts`.
 *
 * Es la pieza de Xokoyotl (`lib/permisos/aparato.ts`) recortada a lo que esta
 * app usa: sin microfono ni ubicacion, que aqui nadie pide.
 */

import { readPushState, enablePush } from './push.js';

export type DeviceResource = 'notifications' | 'camera';

export const DEVICE_RESOURCES: DeviceResource[] = ['notifications', 'camera'];

export type DevicePermission =
  | 'granted'
  /** Todavia no se ha pedido, o no se sabe: se puede pedir. */
  | 'pending'
  /** Se nego. Solo se desbloquea desde el ajuste del sistema. */
  | 'blocked'
  /** Lo cierra la configuracion del servidor (`Permissions-Policy`): error nuestro. */
  | 'forbidden'
  /** iPhone o iPad en Safari: los avisos solo llegan con la app instalada. */
  | 'needs-install'
  /** Este aparato o navegador no lo tiene. */
  | 'unavailable';

export type DevicePermissions = Record<DeviceResource, DevicePermission>;

/**
 * ¿La pagina tiene permitido pedir esta funcion?
 *
 * No es lo mismo que la persona haya dicho que no: si el servidor mandara una
 * `Permissions-Policy` que la cierra, el navegador la niega sin preguntar y con
 * el mismo error que un "no" de la persona. A Xokoyotl le paso con el microfono
 * todo un dia, y la app culpaba al telefono. Distinguirlo es lo que impide
 * mandar a alguien a sus Ajustes por un error nuestro. Ante la duda —el
 * navegador no sabe decirlo— se pide, y el error de verdad llega de
 * `getUserMedia`.
 */
export function policyAllows(feature: 'camera'): boolean {
  if (typeof document === 'undefined') return true;
  const d = document as unknown as {
    permissionsPolicy?: { allowsFeature(f: string): boolean };
    featurePolicy?: { allowsFeature(f: string): boolean };
  };
  const policy = d.permissionsPolicy ?? d.featurePolicy;
  if (!policy) return true;
  try {
    return policy.allowsFeature(feature);
  } catch {
    return true;
  }
}

/** Lo que se le dice a la persona cuando el cierre es nuestro. */
export const FORBIDDEN_BY_US =
  'Es un error del sistema, no de tu aparato: la configuracion del servidor tiene cerrada esta funcion. Avisale a Aziru.';

/** El estado del navegador, traducido. Sin la API, no se sabe: se puede pedir. */
async function queryCamera(): Promise<DevicePermission> {
  if (!navigator.permissions?.query) return 'pending';
  try {
    const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
    return status.state === 'granted' ? 'granted' : status.state === 'denied' ? 'blocked' : 'pending';
  } catch {
    // Safari y Firefox no conocen el nombre `camera` y lanzan: se pide y se ve.
    return 'pending';
  }
}

async function readCamera(): Promise<DevicePermission> {
  if (!policyAllows('camera')) return 'forbidden';
  // La camara del navegador exige https o localhost (CLAUDE.md §13).
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) return 'unavailable';
  return queryCamera();
}

/**
 * Los avisos tienen su propia pregunta: no basta con el permiso, hace falta la
 * suscripcion de este aparato. "Dado" significa que de verdad llegan aqui.
 */
async function readNotifications(): Promise<DevicePermission> {
  const state = await readPushState();
  switch (state.kind) {
    case 'unsupported':
      return state.support === 'install' ? 'needs-install' : 'unavailable';
    case 'blocked':
      return 'blocked';
    case 'on':
      return 'granted';
    case 'off':
    case 'no-worker':
      return 'pending';
  }
}

export async function readDevicePermissions(): Promise<DevicePermissions> {
  if (typeof navigator === 'undefined') return { notifications: 'unavailable', camera: 'unavailable' };
  const [notifications, camera] = await Promise.all([readNotifications(), readCamera()]);
  return { notifications, camera };
}

/**
 * Avisa cuando el aparato cambia el permiso de la camara —al volver de Ajustes,
 * por ejemplo— donde el navegador lo sabe decir. Los avisos no entran: su
 * estado es la suscripcion, y se relee al volver a la app (`visibilitychange`).
 * Devuelve como dejar de escuchar.
 */
export function onPermissionChange(callback: () => void): () => void {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return () => undefined;
  let alive = true;
  let status: PermissionStatus | null = null;
  navigator.permissions
    .query({ name: 'camera' as PermissionName })
    .then((s) => {
      if (!alive) return;
      status = s;
      s.onchange = callback;
    })
    .catch(() => undefined);
  return () => {
    alive = false;
    if (status) status.onchange = null;
  };
}

/**
 * Pide los que falten, en una sola pasada y desde UN toque.
 *
 * Los avisos van primero y SIN ninguna espera por delante: Safari solo enseña
 * ese cuadro si la peticion sale directo del gesto. La camara despues; lo que
 * se abre se cierra en el acto, porque aqui solo se pide el permiso.
 *
 * `include` dice cuales tocan en este aparato: en la computadora del mostrador
 * la camara no se usa —se escanea con el lector USB— y no tiene caso pedirla.
 */
export async function requestPending(
  before: DevicePermissions,
  include: Record<DeviceResource, boolean>,
): Promise<DevicePermissions> {
  const notifications =
    include.notifications && before.notifications === 'pending' ? enablePush() : null;

  if (include.camera && before.camera === 'pending') {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      // Negada o sin camara: lo dice la consulta de despues.
    }
  }
  if (notifications) await notifications.catch(() => undefined);

  return readDevicePermissions();
}
